import Fraction from 'fraction.js';

export const MAX_SAFE_PRISMA_NUMBER = '9999999999.99999';

/** Exact rational for the wire / persistence. */
export interface RationalParts {
	/** Sign: 1 or -1 (0 is represented as sign 1, numerator 0). */
	s: 1 | -1;
	/** Numerator, non-negative. */
	n: string;
	/** Denominator, positive. */
	d: string;
}

type DecimalLike =
	| number
	| string
	| bigint
	| HighPrecisionDecimal
	| Fraction
	| RationalParts
	| { toFixed: (dp?: number) => string };

const C_TEN = 10n;

/**
 * Expand a JS number to a plain (non-exponential) decimal string, so that the
 * fraction parser sees the same decimal expansion `decimal.js` would have used
 * for a number input (decimal.js builds from the number's decimal string, not
 * from a binary-float approximation). Without this, `fraction.js` would run its
 * Farey approximation on the raw float and could pick a different rational.
 */
const numberToDecimalString = (value: number): string => {
	if (!Number.isFinite(value)) {
		throw new Error(`HighPrecisionDecimal cannot represent ${value}`);
	}
	const str = String(value);
	if (!/e/i.test(str)) {
		return str;
	}
	const negative = str[0] === '-';
	const unsigned = negative ? str.slice(1) : str;
	const [mantissa = '', expPart] = unsigned.toLowerCase().split('e');
	const exp = Number(expPart);
	const [intPart = '', fracPart = ''] = mantissa.split('.');
	const digits = intPart + fracPart;
	const pointPos = intPart.length + exp;

	let result: string;
	if (pointPos <= 0) {
		result = `0.${'0'.repeat(-pointPos)}${digits}`;
	} else if (pointPos >= digits.length) {
		result = digits + '0'.repeat(pointPos - digits.length);
	} else {
		result = `${digits.slice(0, pointPos)}.${digits.slice(pointPos)}`;
	}
	return negative ? `-${result}` : result;
};

/**
 * Exact rational number used throughout the valuation engine.
 *
 * Backed by an exact `fraction.js` rational (`{ s, n, d }` as `bigint`); every
 * arithmetic operation is exact, so there is no intermediate drift and no need
 * for the historic `< 1e-10 -> 0` collapse hack. We only fold down to a finite
 * decimal at the edges (`toFixed` for the DB / display, `toRational` for the
 * wire).
 *
 * The class deliberately keeps the method names of the previous `decimal.js`
 * subclass so call sites are unchanged; the (now misleading) class name is
 * renamed in a separate, cosmetic pass.
 */
export class HighPrecisionDecimal {
	private readonly frac: Fraction;

	constructor(value: DecimalLike = 0) {
		this.frac = HighPrecisionDecimal.coerce(value);
	}

	private static coerce(value: DecimalLike): Fraction {
		if (value instanceof HighPrecisionDecimal) {
			return value.frac;
		}
		if (value instanceof Fraction) {
			return value;
		}
		if (typeof value === 'number') {
			return new Fraction(numberToDecimalString(value));
		}
		if (typeof value === 'bigint') {
			return new Fraction(value);
		}
		if (typeof value === 'string') {
			return new Fraction(value);
		}
		// Object form: our wire shape { s, n, d }, a raw fraction { n, d }, or a
		// decimal.js / Prisma.Decimal instance (collapse via its decimal string).
		if (value && typeof value === 'object' && 'n' in value && 'd' in value) {
			const parts = value as {
				n: string | number | bigint;
				d: string | number | bigint;
				s?: string | number | bigint;
			};
			const magnitude = BigInt(parts.n);
			const sign = parts.s !== undefined && BigInt(parts.s) < 0n ? -1n : 1n;
			return new Fraction(magnitude * sign, BigInt(parts.d));
		}
		return new Fraction(numberToDecimalString(Number(value.toFixed())));
	}

	// ── arithmetic (exact) ──────────────────────────────────────────────────
	add(value: DecimalLike): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.add(HighPrecisionDecimal.coerce(value)));
	}

	plus(value: DecimalLike): HighPrecisionDecimal {
		return this.add(value);
	}

	sub(value: DecimalLike): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.sub(HighPrecisionDecimal.coerce(value)));
	}

	minus(value: DecimalLike): HighPrecisionDecimal {
		return this.sub(value);
	}

	mul(value: DecimalLike): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.mul(HighPrecisionDecimal.coerce(value)));
	}

	times(value: DecimalLike): HighPrecisionDecimal {
		return this.mul(value);
	}

	div(value: DecimalLike): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.div(HighPrecisionDecimal.coerce(value)));
	}

	dividedBy(value: DecimalLike): HighPrecisionDecimal {
		return this.div(value);
	}

	mod(value: DecimalLike): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.mod(HighPrecisionDecimal.coerce(value)));
	}

	negated(): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.neg());
	}

	abs(): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.abs());
	}

	/** Largest integer <= value (rounds toward -Infinity, matching decimal.js `.floor()`). */
	floor(): HighPrecisionDecimal {
		return new HighPrecisionDecimal(this.frac.floor());
	}

	// ── comparisons ─────────────────────────────────────────────────────────
	isZero(): boolean {
		return this.frac.n === 0n;
	}

	isNegative(): boolean {
		return this.frac.s < 0n && this.frac.n !== 0n;
	}

	eq(value: DecimalLike): boolean {
		return this.frac.equals(HighPrecisionDecimal.coerce(value));
	}

	equals(value: DecimalLike): boolean {
		return this.eq(value);
	}

	gt(value: DecimalLike): boolean {
		return this.frac.gt(HighPrecisionDecimal.coerce(value));
	}

	gte(value: DecimalLike): boolean {
		return this.frac.gte(HighPrecisionDecimal.coerce(value));
	}

	lt(value: DecimalLike): boolean {
		return this.frac.lt(HighPrecisionDecimal.coerce(value));
	}

	lte(value: DecimalLike): boolean {
		return this.frac.lte(HighPrecisionDecimal.coerce(value));
	}

	lessThan(value: DecimalLike): boolean {
		return this.lt(value);
	}

	lessThanOrEqualTo(value: DecimalLike): boolean {
		return this.lte(value);
	}

	greaterThan(value: DecimalLike): boolean {
		return this.gt(value);
	}

	greaterThanOrEqualTo(value: DecimalLike): boolean {
		return this.gte(value);
	}

	/** -1, 0 or 1, matching decimal.js `comparedTo`. */
	comparedTo(value: DecimalLike): number {
		return this.frac.compare(HighPrecisionDecimal.coerce(value));
	}

	static min(a: HighPrecisionDecimal, b: HighPrecisionDecimal): HighPrecisionDecimal {
		return a.lte(b) ? a : b;
	}

	static max(a: HighPrecisionDecimal, b: HighPrecisionDecimal): HighPrecisionDecimal {
		return a.gte(b) ? a : b;
	}

	// ── rounding / conversion (the edges) ─────────────────────────────────────
	/**
	 * Truncate toward zero to `dp` decimal places (decimal.js ROUND_DOWN, which
	 * is the only mode any call site uses). The result stays exact.
	 */
	toDP(dp: number): HighPrecisionDecimal {
		const scale = C_TEN ** BigInt(dp);
		const truncated = (this.frac.n * scale) / this.frac.d; // floor of the magnitude
		return new HighPrecisionDecimal(new Fraction(this.frac.s * truncated, scale));
	}

	/**
	 * Snap a monetary amount to its exact 2dp fraction (WAT-731 follow-up): rounding to
	 * the displayed cent is a backend business rule, done in fractional form so the
	 * serialization edge is a pure `toString()`. Truncates toward zero (conserving — the
	 * sub-cent is retained by the distributor, never created), e.g. `33.3333 -> 3333/100`.
	 * Identical to `toDP(2)`; the name marks intent (this value is finalized money).
	 */
	toMoney(): HighPrecisionDecimal {
		return this.toDP(2);
	}

	/**
	 * Whether this value is already an exact 2dp fraction (denominator divides 100). Backs the
	 * dev/test invariant that every emitted monetary value is 2dp — a non-2dp money value should
	 * fail loudly rather than serialize garbage (`1/3`) through the pure-`toString()` edge.
	 */
	isExact2dp(): boolean {
		return this.eq(this.toMoney());
	}

	/**
	 * Fixed-point string with `dp` decimal places, rounded HALF_UP (round half
	 * away from zero) - byte-identical to the decimal.js `toFixed` this replaces
	 * (the engine cloned decimal.js with default rounding ROUND_HALF_UP).
	 */
	toFixed(dp = 0): string {
		const places = BigInt(dp);
		const scale = C_TEN ** places;
		const scaledNumerator = this.frac.n * scale;
		let quotient = scaledNumerator / this.frac.d;
		const remainder = scaledNumerator % this.frac.d;
		// Round half away from zero: tie (2*remainder === d) rounds up.
		if (remainder * 2n >= this.frac.d) {
			quotient += 1n;
		}

		// Match decimal.js: a negative value keeps its sign even when it rounds to zero ("-0.00").
		// Exact arithmetic yields positive zero where decimal.js drift used to, so this introduces
		// no spurious "-0.00" for genuinely-zero results.
		const negative = this.frac.s < 0n;
		const digits = quotient.toString().padStart(dp + 1, '0');
		const cut = digits.length - dp;
		const intPart = digits.slice(0, cut);
		const result = dp > 0 ? `${intPart}.${digits.slice(cut)}` : intPart;
		return negative ? `-${result}` : result;
	}

	toNumber(): number {
		const value = this.frac.valueOf();
		const { n, d, s } = this.frac;
		// fraction.js divides Number(n) by Number(d), so either side past 2^1024 turns into Infinity.
		if (Number.isFinite(value) && (value !== 0 || n === 0n)) {
			return value;
		}
		const scale = d.toString(2).length - n.toString(2).length + 64;
		const quotient = scale >= 0 ? (n << BigInt(scale)) / d : n / (d << BigInt(-scale));
		return Number(s) * Number(quotient) * 2 ** -scale;
	}

	valueOf(): number {
		return this.toNumber();
	}

	/**
	 * Exact number of decimal places if the value terminates (denominator is a
	 * product of 2s and 5s only), otherwise `null`.
	 */
	private terminatingPlaces(): number | null {
		let denominator = this.frac.d;
		if (denominator === 1n) {
			return 0;
		}
		let twos = 0;
		let fives = 0;
		while (denominator % 2n === 0n) {
			denominator /= 2n;
			twos++;
		}
		while (denominator % 5n === 0n) {
			denominator /= 5n;
			fives++;
		}
		return denominator === 1n ? Math.max(twos, fives) : null;
	}

	/**
	 * Number of decimal places needed to represent the value exactly, or, when
	 * the value is non-terminating (e.g. 1/3), a finite upper bound large enough
	 * that display call sites clamp it to their own max precision.
	 */
	dp(): number {
		return this.terminatingPlaces() ?? NON_TERMINATING_DP;
	}

	/** Exact, canonical rational form for the wire / persistence. */
	toRational(): RationalParts {
		return {
			s: this.frac.s < 0n && this.frac.n !== 0n ? -1 : 1,
			n: this.frac.n.toString(),
			d: this.frac.d.toString(),
		};
	}

	/**
	 * Canonical exact string. A terminating value renders as a plain decimal
	 * (`0.5`, `1234.56`, `2700`); a non-terminating value renders as exact `n/d`
	 * (`1/3`). Always parseable back into a `HighPrecisionDecimal`.
	 *
	 * (The previous decimal.js subclass produced a comma-grouped, 2dp-rounded,
	 * and partly buggy string; human display formatting lives in the frontend
	 * `formatDecimal` helper, never here.)
	 */
	toString(): string {
		if (this.frac.n === 0n) {
			return '0';
		}
		const sign = this.frac.s < 0n ? '-' : '';
		if (this.frac.d === 1n) {
			return `${sign}${this.frac.n}`;
		}
		const places = this.terminatingPlaces();
		if (places !== null) {
			// Exact: a value terminating at `places` has zero remainder there.
			const scale = C_TEN ** BigInt(places);
			const scaled = (this.frac.n * scale) / this.frac.d;
			const digits = scaled.toString().padStart(places + 1, '0');
			const cut = digits.length - places;
			return `${sign}${digits.slice(0, cut)}.${digits.slice(cut)}`;
		}
		return `${sign}${this.frac.n}/${this.frac.d}`;
	}

	/**
	 * `JSON.stringify` hook. A value backed by fraction.js holds its numerator/denominator as `bigint`,
	 * which `JSON.stringify` cannot serialize ("Do not know how to serialize a BigInt") — so any HPD that
	 * reaches an HTTP response boundary without an explicit `.toString()` (e.g. a security definition's
	 * rate nested in the breakpoint payload) would 500 the request. Emit the canonical string instead.
	 * The devalue codecs (worker transfer, graph persistence) use explicit reducers and never reach here.
	 */
	toJSON(): string {
		return this.toString();
	}
}

/**
 * Cap returned by `dp()` for non-terminating values. Big enough to never clip a
 * real display (call sites take `min(dp(), maxPrecision)`), small enough that a
 * stray `toFixed(dp())` stays bounded.
 */
const NON_TERMINATING_DP = 20;
